import { useState, useEffect, useRef, useMemo } from 'react'

import { Image } from 'shopify-buy'
import { useSwipeable } from 'react-swipeable'
import { Box } from '@mui/material'
import LensIcon from '@mui/icons-material/Lens'
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded'
import ArrowBackIosNewRounded from '@mui/icons-material/ArrowBackIosNewRounded'

import { ImageContainer } from '../ImageContainer/index'

interface CarouselProps {
  images: Image[]
  priority?: boolean
  filterImages?: (image: Image, index: number) => boolean
}

const Carousel = ({
  images,
  priority = false,
  filterImages = (image: Image, index: number) => true,
}: CarouselProps) => {
  const [currentImage, setCurrentImage] = useState<number>(0)

  const imagesToUse = useMemo(
    () => images.filter(filterImages),
    [images, filterImages]
  )

  const prevImage = () => {
    const isFirstImage = currentImage === 0
    const newImage = isFirstImage ? imagesToUse.length - 1 : currentImage - 1
    setCurrentImage(newImage)
  }

  const nextImage = () => {
    const isLastImage = currentImage === imagesToUse.length - 1
    const newImage = isLastImage ? 0 : currentImage + 1
    setCurrentImage(newImage)
  }

  const toImage = (index: number) => {
    setCurrentImage(index)
  }

  const handleSwipe = useSwipeable({
    onSwipedLeft: () => nextImage(),
    onSwipedRight: () => prevImage(),
    trackMouse: true,
    preventScrollOnSwipe: true,
  })

  return (
    <Box
      className={`max-w-[1400px] md:h-[500px] h-[380px] w-full m-auto relative overflow-hidden rounded-[16px]`}
    >
      <div
        className="whitespace-nowrap h-full transition ease-in-out delay-150"
        style={{ transform: `translateX(-${currentImage * 100}%)` }}
      >
        {imagesToUse.map((image, i) => (
          <ImageContainer
            key={`image-${i}`}
            priority={priority}
            src={image.src}
            handleSwipe={handleSwipe}
            roundedClass="transition ease-in-out delay-150 inline-flex items-center justify-center"
          />
        ))}
      </div>
      <div className="absolute top-[50%] -translate-x-0 translate-y-[-50%] left-5 text-4xl p-2 cursor-pointer text-[50px]">
        <ArrowBackIosNewRounded
          onClick={prevImage}
          sx={{
            backgroundColor: '#2C2C2E',
            borderRadius: '50%',
            padding: '5px',
          }}
        />
      </div>
      <div className="absolute top-[50%] -translate-x-0 translate-y-[-50%] right-5 text-4xl p-2 cursor-pointer  w-[50px] h-[50px]">
        <ArrowForwardIosRoundedIcon
          onClick={nextImage}
          style={{
            backgroundColor: '#2C2C2E',
            borderRadius: '50%',
            padding: '5px',
          }}
        />
      </div>
      <div className="absolute bottom-0 flex gap-2 justify-center w-full h-8  opacity-75">
        {imagesToUse.map((_, i) => (
          <LensIcon
            className="cursor-pointer"
            key={i}
            sx={{
              fontSize: '10px',
              color: `${i === currentImage ? '#8E8E93' : '#3A3A3C'}`,
            }}
            onClick={() => toImage(i)}
          />
        ))}
      </div>
    </Box>
  )
}

export default Carousel
